import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { BlogPost, LoadingState, PaginatedResponse } from '../../types';
import { blogService } from '../../services/blogService';

interface BlogState extends LoadingState {
  posts: BlogPost[];
  featuredPosts: BlogPost[];
  selectedPost: BlogPost | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  likes: {
    [blogId: string]: {
      likesCount: number;
      userLiked: boolean;
      isLoading: boolean;
    };
  };
}

const initialState: BlogState = {
  posts: [],
  featuredPosts: [],
  selectedPost: null,
  pagination: null,
  likes: {},
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchBlogPosts = createAsyncThunk(
  'blog/fetchBlogPosts',
  async (params: { page?: number; limit?: number; sort?: string; order?: 'asc' | 'desc' }, { rejectWithValue }) => {
    try {
      const response = await blogService.getPublishedBlogs(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch blog posts');
    }
  }
);

export const fetchFeaturedPosts = createAsyncThunk(
  'blog/fetchFeaturedPosts',
  async (params: { limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await blogService.getFeaturedBlogs({ limit: params.limit || 5 });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch featured posts');
    }
  }
);

export const fetchBlogPostById = createAsyncThunk(
  'blog/fetchBlogPostById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await blogService.getBlogById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch blog post');
    }
  }
);

// Blog Like Thunks
export const fetchBlogLikes = createAsyncThunk(
  'blog/fetchBlogLikes',
  async (blogId: string, { rejectWithValue }) => {
    try {
      const response = await blogService.getBlogLikes(blogId);
      return { 
        blogId, 
        likesCount: response.data.likesCount,
        userLiked: response.data.userLiked 
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch blog likes');
    }
  }
);

export const toggleBlogLike = createAsyncThunk(
  'blog/toggleBlogLike',
  async (blogId: string, { rejectWithValue }) => {
    try {
      const response = await blogService.toggleBlogLike(blogId);
      return { blogId, isLiked: response.data.isLiked };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle blog like');
    }
  }
);

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedPost: (state, action: PayloadAction<BlogPost | null>) => {
      state.selectedPost = action.payload;
    },
    clearPosts: (state) => {
      state.posts = [];
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Blog Posts
      .addCase(fetchBlogPosts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBlogPosts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.posts = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchBlogPosts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Featured Posts
      .addCase(fetchFeaturedPosts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedPosts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featuredPosts = action.payload.data;
      })
      .addCase(fetchFeaturedPosts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Blog Post by ID
      .addCase(fetchBlogPostById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBlogPostById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPost = action.payload;
      })
      .addCase(fetchBlogPostById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Blog Likes
      .addCase(fetchBlogLikes.pending, (state, action) => {
        const blogId = action.meta.arg;
        if (!state.likes[blogId]) {
          state.likes[blogId] = { likesCount: 0, userLiked: false, isLoading: false };
        }
        state.likes[blogId].isLoading = true;
      })
      .addCase(fetchBlogLikes.fulfilled, (state, action) => {
        const { blogId, likesCount, userLiked } = action.payload;
        state.likes[blogId] = {
          likesCount,
          userLiked,
          isLoading: false
        };
      })
      .addCase(fetchBlogLikes.rejected, (state, action) => {
        const blogId = action.meta.arg;
        if (state.likes[blogId]) {
          state.likes[blogId].isLoading = false;
        }
        state.error = action.payload as string;
      })
      // Toggle Blog Like
      .addCase(toggleBlogLike.pending, (state, action) => {
        const blogId = action.meta.arg;
        if (state.likes[blogId]) {
          state.likes[blogId].isLoading = true;
        }
      })
      .addCase(toggleBlogLike.fulfilled, (state, action) => {
        const { blogId, isLiked } = action.payload;
        if (state.likes[blogId]) {
          // Update user like status and count
          const wasLiked = state.likes[blogId].userLiked;
          state.likes[blogId].userLiked = isLiked;
          
          // Adjust count based on like toggle
          if (isLiked && !wasLiked) {
            state.likes[blogId].likesCount += 1;
          } else if (!isLiked && wasLiked) {
            state.likes[blogId].likesCount -= 1;
          }
          
          state.likes[blogId].isLoading = false;
        }
      })
      .addCase(toggleBlogLike.rejected, (state, action) => {
        const blogId = action.meta.arg;
        if (state.likes[blogId]) {
          state.likes[blogId].isLoading = false;
        }
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setSelectedPost, clearPosts } = blogSlice.actions;
export default blogSlice.reducer;
